import { useCallback, useMemo } from "react";

import { useLocalStorage } from "@/lib/hooks/useLocalStorage";

interface UseVisibleEntitiesParams<T> {
  items: T[];
  storageKey: string;
  getKey: (item: T) => string;
  /**
   * Initial visible set when the user has not made an explicit choice yet
   * (e.g. all items, or top-N by some sort). Once the user toggles anything
   * the selection becomes explicit and this default no longer applies.
   */
  defaultSelection: (items: T[]) => string[];
}

/**
 * Persists a user's visibility filter for a collection of entities. `null` is
 * the sentinel for "no explicit choice — show defaultSelection(items)". On the
 * first toggle the selection becomes an explicit array and is locked in until
 * `reset()` returns to the default. Used by the home page for both segment
 * lines on the count chart and slices on the distribution donut.
 */
export function useVisibleEntities<T>({
  items,
  storageKey,
  getKey,
  defaultSelection,
}: UseVisibleEntitiesParams<T>) {
  const [selected, setSelected] = useLocalStorage<string[] | null>(
    storageKey,
    null,
  );

  const filterActive = selected !== null;

  const defaultKeys = useMemo(
    () => defaultSelection(items),
    [items, defaultSelection],
  );

  const isSelected = useCallback(
    (key: string) =>
      selected === null ? defaultKeys.includes(key) : selected.includes(key),
    [selected, defaultKeys],
  );

  const toggle = useCallback(
    (key: string) => {
      setSelected((prev) => {
        const base = prev ?? defaultKeys;
        return base.includes(key)
          ? base.filter((x) => x !== key)
          : [...base, key];
      });
    },
    [defaultKeys, setSelected],
  );

  const reset = useCallback(() => setSelected(null), [setSelected]);

  // Resolve the keys that are currently visible — useful for downstream filtering.
  const visibleKeys = useMemo(
    () => (selected ?? defaultKeys),
    [selected, defaultKeys],
  );

  const visibleItems = useMemo(
    () => items.filter((item) => visibleKeys.includes(getKey(item))),
    [items, visibleKeys, getKey],
  );

  return {
    selected,
    filterActive,
    isSelected,
    toggle,
    reset,
    visibleKeys,
    visibleItems,
  };
}
